import { useEffect, useRef } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

export function useWizardPersistence<T extends FieldValues>(
  form: UseFormReturn<T>,
  persistKey: string | undefined,
  exclude: readonly (keyof T & string)[] = [],
): void {
  const excludeRef = useRef(exclude);
  excludeRef.current = exclude;

  useEffect(() => {
    if (!persistKey) return;
    try {
      const raw = localStorage.getItem(persistKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<T>;
      form.reset({ ...form.getValues(), ...parsed } as T);
    } catch {
      // ignore corrupt storage
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistKey]);

  useEffect(() => {
    if (!persistKey) return;
    const subscription = form.watch((values) => {
      const clone: Record<string, unknown> = { ...values };
      for (const key of excludeRef.current) {
        delete clone[key];
      }
      try {
        localStorage.setItem(persistKey, JSON.stringify(clone));
      } catch {
        // quota or privacy-mode
      }
    });
    return () => subscription.unsubscribe();
  }, [form, persistKey]);
}

export function clearWizardPersistence(persistKey: string | undefined): void {
  if (!persistKey) return;
  try {
    localStorage.removeItem(persistKey);
  } catch {
    // ignore
  }
}
